import React from 'react';
import antiCopyHero from './assets/anti-copy-context-hero-generated.png';
import antiCopyHeroMobile from './assets/anti-copy-context-hero-mobile-generated.png';
import { FUNNEL_DISCLAIMER } from './funnelVariants';
import './RiskInvalidationFunnel.css';
import './AntiCopyContextFunnel.css';

const guardrails = ['Free education', 'No payment on this page', 'Action remains yours'];

const contextSteps = [
  {
    number: '01',
    title: 'Reason',
    copy: 'What makes this market idea worth attention before the call matters?',
  },
  {
    number: '02',
    title: 'Invalidation',
    copy: 'What condition proves the idea wrong before you follow it?',
  },
  {
    number: '03',
    title: 'Fit',
    copy: 'What risk fits your own rules if the call fails?',
  },
];

const telegramNotes = [
  'Reason note before the setup',
  'Invalidation reminder before action',
  'Risk context before following anything',
];

const decisionChecks = [
  {
    title: 'Can you explain the reason?',
    copy: 'A copied message is not enough. The setup needs context before it becomes your decision.',
  },
  {
    title: 'Can you find the failure point?',
    copy: 'If there is no invalidation, there is no clean way to know when the idea stopped working.',
  },
  {
    title: 'Can you make it fit your rules?',
    copy: 'The risk belongs to you, so the decision has to fit your process before anyone else acts.',
  },
];

const comparisonRows = [
  ['Random call only', 'Fast message, missing reason, failure point, and your risk.'],
  ['Context before copy', 'Reason, invalidation, and fit are visible before any action.'],
];

function TelegramGlyph() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M21.7 3.4 2.9 10.7c-1.2.5-1.2 1.2-.2 1.5l4.8 1.5 1.9 5.8c.2.7.4 1 .8 1 .4 0 .6-.2.9-.5l2.6-2.5 5.3 3.9c1 .5 1.6.2 1.9-.9l3.4-15.9c.3-1.3-.5-1.8-1.6-1.3Zm-4 3.6-8.3 7.5-.3 3.1-1.1-3.6 9.7-7Z" />
    </svg>
  );
}

function AntiCopyVisual() {
  return (
    <figure
      className="risk-funnel-visual anti-copy-funnel-visual"
      aria-label="Anti-copying context visual"
      data-track-id="anti-copy-context-hero-visual"
      data-track-hover="anti-copy-context-hero-visual"
    >
      <picture>
        <source srcSet={antiCopyHeroMobile} media="(max-width: 720px)" />
        <img
          src={antiCopyHero}
          alt="Dark crypto chart with a random calls warning, copy button, and labels for reason, risk, and invalidation."
          width="1586"
          height="992"
          loading="eager"
          decoding="async"
        />
      </picture>
    </figure>
  );
}

const PrimaryCta = React.forwardRef(function PrimaryCta(
  {
    telegramCtaProps,
    children,
    className = '',
    trackId,
    hidden = false,
  },
  ref
) {
  return (
    <a
      {...telegramCtaProps}
      ref={ref}
      className={`risk-funnel__cta ${className}`.trim()}
      aria-hidden={hidden ? 'true' : telegramCtaProps['aria-hidden']}
      tabIndex={hidden ? -1 : telegramCtaProps.tabIndex}
      data-track-id={trackId}
      data-track-hover={trackId}
      data-track-cta
    >
      <TelegramGlyph />
      <span>{children}</span>
    </a>
  );
});

export function AntiCopyContextFunnel({
  variant,
  rootRef,
  heroCtaRef,
  finalCtaRef,
  telegramCtaProps,
  telegramCtaLabel,
  showMobileCta,
}) {
  return (
    <main
      ref={rootRef}
      className="risk-invalidation-funnel anti-copy-context-funnel"
      data-funnel-variant={variant.key}
      data-visual-funnel="anti_copy_context"
      data-track-section="page"
    >
      <header className="risk-funnel-header" data-track-section="header">
        <div className="risk-funnel-header__brand" data-track-id="anti-copy-brand">
          <img
            className="risk-funnel-header__mark"
            src="/assets/images/logo.jpg"
            alt=""
            aria-hidden="true"
            width="38"
            height="38"
            decoding="async"
          />
          <span>CRYPTOXNFT</span>
        </div>
        <PrimaryCta
          telegramCtaProps={telegramCtaProps}
          className="risk-funnel-header__cta"
          trackId="header-telegram-cta"
        >
          {telegramCtaLabel}
        </PrimaryCta>
      </header>

      <section className="risk-funnel-hero" data-track-section="hero">
        <div className="risk-funnel-hero__inner">
          <div className="risk-funnel-hero__copy">
            <p
              className="risk-funnel-eyebrow"
              data-track-id="hero-eyebrow"
              data-track-hover="hero-eyebrow"
            >
              Anti-copying crypto education
            </p>
            <h1 data-track-id="hero-headline">
              Understand the setup before you follow the call.
            </h1>
            <p className="risk-funnel-hero__lead" data-track-id="hero-intro">
              Random calls remove the reason, invalidation, and risk from the decision. The free
              group teaches what to check before following anything.
            </p>
            <div className="risk-funnel-hero__guards" aria-label="Telegram expectations">
              {guardrails.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <div className="risk-funnel-hero__actions">
              <PrimaryCta
                ref={heroCtaRef}
                telegramCtaProps={telegramCtaProps}
                className="risk-funnel-hero__cta"
                trackId="hero-telegram-cta"
              >
                {telegramCtaLabel}
              </PrimaryCta>
              <p>
                Opens the free Telegram group for education notes and context checks before your
                next decision.
              </p>
            </div>
          </div>

          <AntiCopyVisual />
        </div>
      </section>

      <section className="risk-funnel-plan" data-track-section="three-line-plan">
        <div className="risk-funnel-section-heading">
          <span>Three-line context check</span>
          <h2 data-track-id="three-line-plan-heading">
            A call is not actionable until these lines are clear.
          </h2>
          <p data-track-id="three-line-plan-copy">
            The free group keeps the decision focused on the reason, failure point, and risk before
            a market call becomes your action.
          </p>
        </div>
        <div className="risk-funnel-plan__grid">
          {contextSteps.map((step) => (
            <article
              className="risk-funnel-step"
              key={step.title}
              data-track-id={`plan-step-${step.number}`}
              data-track-hover={`plan-step-${step.number}`}
            >
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="risk-funnel-contrast" data-track-section="problem-contrast">
        <div className="risk-funnel-contrast__copy">
          <span>Why this slows the copy</span>
          <h2 data-track-id="contrast-heading">Signals without context are just noise.</h2>
          <p data-track-id="contrast-copy">
            The free group should feel like an education layer for better judgment, not a shortcut
            around your own responsibility.
          </p>
        </div>
        <div className="risk-funnel-contrast__table" aria-label="Decision contrast">
          {comparisonRows.map(([label, value], index) => (
            <div
              className="risk-funnel-contrast__row"
              key={label}
              data-track-id={`contrast-row-${index + 1}`}
              data-track-hover={`contrast-row-${index + 1}`}
            >
              <strong>{label}</strong>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="risk-funnel-telegram" data-track-section="telegram-preview">
        <div className="risk-funnel-telegram__panel">
          <div className="risk-funnel-telegram__copy">
            <span>What opens next</span>
            <h2 data-track-id="preview-heading">
              Free Telegram education, focused on reason before action.
            </h2>
            <p data-track-id="preview-summary">
              The next step is a free group with market context, risk reminders, and decision
              checks. No payment is requested on this page.
            </p>
            <PrimaryCta
              telegramCtaProps={telegramCtaProps}
              className="risk-funnel-telegram__cta"
              trackId="preview-telegram-cta"
            >
              {telegramCtaLabel}
            </PrimaryCta>
          </div>
          <div
            className="risk-funnel-telegram__preview"
            aria-label="Telegram preview"
            data-track-id="telegram-preview-panel"
            data-track-hover="telegram-preview-panel"
          >
            <div className="risk-funnel-telegram__bar">
              <strong>Free Education Group</strong>
              <span>Reason first</span>
            </div>
            <ul>
              {telegramNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            <div className="risk-funnel-telegram__stats" aria-label="Community stats">
              <span>
                <strong>45K+</strong>
                Members
              </span>
              <span>
                <strong>Free</strong>
                Education
              </span>
              <span>
                <strong>Daily</strong>
                Updates
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="risk-funnel-filter" data-track-section="decision-filter">
        <div className="risk-funnel-section-heading">
          <span>Decision filter</span>
          <h2 data-track-id="filter-heading">
            Before you follow, answer the questions the ad showed.
          </h2>
        </div>
        <div className="risk-funnel-filter__grid">
          {decisionChecks.map((check, index) => (
            <article
              className="risk-funnel-check"
              key={check.title}
              data-track-id={`check-${index + 1}`}
              data-track-hover={`check-${index + 1}`}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{check.title}</h3>
              <p>{check.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="risk-funnel-final" data-track-section="final-cta">
        <div className="risk-funnel-final__content">
          <span>Free Telegram group</span>
          <h2 data-track-id="final-heading">Join free. Read the reason. Decide with rules.</h2>
          <p data-track-id="final-copy">
            Use the group for education notes and risk reminders before deciding whether any setup
            fits your own rules.
          </p>
          <div className="risk-funnel-final__action">
            <PrimaryCta
              ref={finalCtaRef}
              telegramCtaProps={telegramCtaProps}
              trackId="final-telegram-cta"
            >
              {telegramCtaLabel}
            </PrimaryCta>
          </div>
          <p className="risk-funnel-final__disclaimer" data-track-id="final-disclaimer">
            {FUNNEL_DISCLAIMER}
          </p>
        </div>
      </section>

      <PrimaryCta
        telegramCtaProps={telegramCtaProps}
        className={`risk-funnel-mobile-cta${showMobileCta ? ' is-visible' : ''}`}
        trackId="mobile-sticky-telegram-cta"
        hidden={!showMobileCta}
      >
        {telegramCtaLabel}
      </PrimaryCta>
    </main>
  );
}

export default AntiCopyContextFunnel;
